import { cloneJson } from './pack-model.js'

/**
 * 条目被整本内容里哪些位置引用（移植 Demo 编辑器 StoryEditor.vue 的 references_for）。
 * 位置写成与严格校验错误同一种点分路径（如 `facts.0.observer_ids`），编辑器据此定位分区并聚焦控件。
 * @param {Record<string, any>} pack
 * @param {'characters'|'locations'|'items'|'facts'|'opening'} kind
 * @param {string} target 条目 id（开场段落传空串）
 * @returns {string[]}
 */
export function referencesFor(pack, kind, target) {
  const refs = []
  const add = (present, path) => {
    if (present) refs.push(path)
  }
  const cueTriggers = () => {
    for (const [person_index, person] of (pack.characters ?? []).entries()) {
      for (const [cue_index, cue] of (person.performance_cues ?? []).entries()) {
        add(cue.trigger?.entity_id === target, `characters.${person_index}.performance_cues.${cue_index}.trigger.entity_id`)
      }
    }
  }
  if (kind === 'characters') {
    add(pack.player_id === target, 'player_id')
    add(Object.hasOwn(pack.initial_state?.positions ?? {}, target), `initial_state.positions.${target}`)
    for (const [item_id, state] of Object.entries(pack.initial_state?.items ?? {})) {
      add(state?.holder_id === target, `initial_state.items.${item_id}.holder_id`)
    }
    for (const [i, fact] of (pack.facts ?? []).entries()) add(fact.observer_ids?.includes(target), `facts.${i}.observer_ids`)
    for (const [i, relation] of (pack.relations ?? []).entries()) {
      add(relation.from_id === target, `relations.${i}.from_id`)
      add(relation.to_id === target, `relations.${i}.to_id`)
      add(relation.observer_ids?.includes(target), `relations.${i}.observer_ids`)
    }
    for (const [i, segment] of (pack.opening?.segments ?? []).entries()) add(segment.speaker_id === target, `opening.segments.${i}.speaker_id`)
    for (const [i, scene] of (pack.scene_templates ?? []).entries()) add(scene.present_ids?.includes(target), `scene_templates.${i}.present_ids`)
    cueTriggers()
  }
  if (kind === 'locations') {
    for (const [person_index, person] of (pack.characters ?? []).entries()) {
      for (const [rule_index, rule] of (Array.isArray(person.drama_presets?.locations) ? person.drama_presets.locations : []).entries()) {
        add(rule.location_id === target, `characters.${person_index}.drama_presets.locations.${rule_index}.location_id`)
      }
    }
    for (const [person_id, location_id] of Object.entries(pack.initial_state?.positions ?? {})) {
      add(location_id === target, `initial_state.positions.${person_id}`)
    }
    for (const [item_id, state] of Object.entries(pack.initial_state?.items ?? {})) {
      add(state?.location_id === target, `initial_state.items.${item_id}.location_id`)
    }
    for (const [i, scene] of (pack.scene_templates ?? []).entries()) add(scene.location_id === target, `scene_templates.${i}.location_id`)
    cueTriggers()
  }
  if (kind === 'items') {
    add(Object.hasOwn(pack.initial_state?.items ?? {}, target), `initial_state.items.${target}`)
    cueTriggers()
  }
  if (kind === 'facts') {
    for (const [person_index, person] of (pack.characters ?? []).entries()) {
      for (const [cue_index, cue] of (person.performance_cues ?? []).entries()) {
        add(cue.fact_ids?.includes(target), `characters.${person_index}.performance_cues.${cue_index}.fact_ids`)
      }
    }
  }
  return refs
}

/** 只属于被删条目自己的位置，删除时连带清掉、不算阻塞：人物的起始位置、物品的开场状态。 */
function ownedPath(kind, target) {
  if (kind === 'characters') return `initial_state.positions.${target}`
  if (kind === 'items') return `initial_state.items.${target}`
  return null
}

/**
 * 删除一个条目（Demo 的 remove_entity / remove_opening）：别处还有引用就不删，返回这些位置；
 * 没有就返回删除后的新作品，并连带清掉它自己的起始位置或开场状态。玩家角色被 player_id 引用，永远删不掉。
 * @returns {{ pack: Record<string, any>|null, blocking: string[] }}
 */
export function removeEntry(pack, kind, index) {
  const list = kind === 'opening' ? pack.opening?.segments ?? [] : pack[kind] ?? []
  if (index < 0 || index >= list.length) return { pack: null, blocking: [] }
  const target = kind === 'opening' ? '' : list[index].id
  const owned = ownedPath(kind, target)
  const blocking = referencesFor(pack, kind, target).filter(path => path !== owned)
  if (blocking.length > 0) return { pack: null, blocking }
  const next = cloneJson(pack)
  if (kind === 'opening') {
    next.opening.segments.splice(index, 1)
    next.initial_threads = []
  }
  else next[kind].splice(index, 1)
  if (kind === 'characters' && next.initial_state?.positions) delete next.initial_state.positions[target]
  if (kind === 'characters' && next.initial_threads) {
    next.initial_threads = next.initial_threads.map(thread => ({ ...thread, participant_ids: thread.participant_ids.filter(id => id !== target) }))
      .filter(thread => thread.participant_ids.length)
  }
  if (kind === 'items' && next.initial_state?.items) delete next.initial_state.items[target]
  return { pack: next, blocking: [] }
}
